import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { Field, Form } from '@components/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUsers } from '@hooks/users';
import { Box, Button, Stack, Typography } from '@mui/material';
import handleRequestError from '@utils/handleRequestError';

const initialValues = {
  email: '',
};

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols'),
});

export function ForgotPassword() {
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const methods = useForm({
    resolver: yupResolver(ForgotPasswordSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  });

  const { formState } = methods;

  const { passwordReset: resetPassword, isPending: resetPasswordPending } = useUsers({
    onPasswordReset: () => {
      setEmailSent(true);
    },
    onError: handleRequestError,
  });

  const onSubmit = (values) => {
    doResetPassword(values.email);
  };

  const handleResend = async () => {
    if (!submittedEmail) return;
    try {
      setIsResending(true);
      await doResetPassword(submittedEmail);
    } finally {
      setIsResending(false);
    }
  };

  // helper to avoid repeating requestOptions
  const doResetPassword = (email) =>
    resetPassword(
      {
        email,
        requestOptions: {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      },
      { onSuccess: () => setSubmittedEmail(email) }
    );

  return (
    <>
      {!emailSent ? (
        <Box>
          <Stack sx={{ textAlign: 'center', mb: 5, minWidth: { xs: '100%', sm: 400 }, mx: 'auto' }}>
            <Typography
              component="h3"
              sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
              variant="h5"
            >
              Forgotten Password ?
            </Typography>
            <Box
              sx={(theme) => ({
                color: 'text.secondary',
                fontWeight: theme.typography.fontWeightBold,
              })}
            >
              Enter your email to reset your password
            </Box>
          </Stack>
          <Form methods={methods} onSubmit={onSubmit}>
            {formState?.errors?.root?.server && (
              <Box sx={{ mb: 10, bgcolor: 'error.light', p: 2, borderRadius: 1 }}>
                <Box sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
                  {formState.errors.root.server.message}
                </Box>
              </Box>
            )}
            <Box sx={{ mb: 2, width: '100%', mx: 'auto' }}>
              <Field.Text
                name="email"
                placeholder="Email"
                sx={{
                  width: '100%',
                }}
                type="email"
              />
            </Box>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              sx={{ width: '100%', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}
            >
              <Button
                component={Link}
                id="kt_login_forgot_cancel"
                sx={(theme) => ({
                  backgroundColor: 'primary.light',
                  fontWeight: theme.typography.fontWeightBold,
                  px: 4,

                  minWidth: 0,
                  boxSizing: 'border-box',
                })}
                to="/auth"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                disabled={formState.isSubmitting}
                id="kt_login_forgot_submit"
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightBold,
                  px: 4,
                  minWidth: 0,
                  boxSizing: 'border-box',
                })}
                type="submit"
                variant="contained"
              >
                {resetPasswordPending ? 'Submitting...' : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </Box>
      ) : (
        <Box>
          <Stack sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{ mb: 2, width: '100%', px: { xs: 2, sm: 0 }, mx: 'auto' }}>
              <Typography
                component="h3"
                sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
                variant="h5"
              >
                Email Sent
              </Typography>
              <Box
                sx={(theme) => ({
                  color: 'text.secondary',
                  fontWeight: theme.typography.fontWeightBold,
                })}
              >
                Please check your email for a link to reset your password.
              </Box>
            </Box>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              sx={{ width: '100%', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}
            >
              <Button
                component={Link}
                id="kt_login_forgot_cancel"
                sx={(theme) => ({
                  backgroundColor: 'primary.light',
                  fontWeight: theme.typography.fontWeightBold,
                  px: 4,
                  flex: { xs: '0 0 48%', sm: '0 0 auto' },
                  minWidth: 0,
                  boxSizing: 'border-box',
                })}
                to="/auth"
                variant="contained"
              >
                Back to Login
              </Button>

              <Button
                disabled={!submittedEmail || isResending}
                id="kt_login_forgot_resend"
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightBold,
                  px: 4,
                  flex: { xs: '0 0 48%', sm: '0 0 auto' },
                  minWidth: 0,
                  boxSizing: 'border-box',
                })}
                variant="outlined"
                onClick={handleResend}
              >
                {isResending ? 'Resending...' : 'Resend Email'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  );
}
